import {
  Title,
  ScrollArea,
  TextInput,
  Select,
  Button,
  Group,
  Transition,
  Alert,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useFieldFormContext } from '../../hooks/use-field-form/field-form';

import classes from './field-form.module.css';

import { getDirtyValues } from '@data-access';
import { CreateFieldDto, useValues } from '@fields/data-access';

export interface FieldFormProps {
  type: 'create' | 'edit';
  onSubmit: (
    data:
      | { type: 'create'; values: CreateFieldDto }
      | { type: 'edit'; values: Partial<CreateFieldDto> },
  ) => void;
  loading?: boolean;
}

export function FieldForm({
  type,
  onSubmit,

  loading,
}: FieldFormProps) {
  const { t, i18n } = useTranslation();
  const { history, navigate } = useRouter();
  const form = useFieldFormContext();

  const options = useValues();

  const handleSubmit = (values: CreateFieldDto) => {
    if (type === 'create') {
      onSubmit({ type: 'create', values });
    }
    if (type === 'edit') {
      const dirtyValues = getDirtyValues(form);
      onSubmit({ type: 'edit', values: dirtyValues });
    }
  };

  const handleBack = () => {
    if (history.location.state.key) {
      history.back();
    } else
      navigate({
        to: '/fields',
      });
  };

  return (
    <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
      <Title order={2}>
        {type === 'create'
          ? t('field.form.createTitle')
          : t('field.form.editTitle')}
      </Title>

      <ScrollArea className={classes.inputsContainer}>
        <TextInput
          label={t('field.form.nameLabel')}
          required
          {...form.getInputProps('name')}
        />

        <Select
          allowDeselect={false}
          data={options.data?.farm}
          label={t('field.form.farmLabel')}
          mt="sm"
          required
          searchable
          {...form.getInputProps('farm')}
        />

        <Select
          allowDeselect={true}
          clearable
          data={options.data?.species(i18n.language)}
          label={t('field.form.cropLabel')}
          mt="sm"
          searchable
          {...form.getInputProps('species')}
        />

        <Select
          allowDeselect={true}
          clearable
          data={options.data?.variety.filter(
            (v) => v.parent.toString() === form.getValues().species,
          )}
          label={t('field.form.varietyLabel')}
          mt="sm"
          searchable
          {...form.getInputProps('variety')}
        />
      </ScrollArea>

      <Group justify="flex-end">
        <Transition
          duration={400}
          mounted={!!form.errors['boundaries']}
          timingFunction="ease"
          transition="fade-up"
        >
          {(styles) => (
            <Alert
              color="red"
              icon={<IconInfoCircle />}
              onClose={() => form.clearFieldError('boundaries')}
              style={styles}
              title={t('field.form.boundaryRequiredTitle')}
              variant="filled"
              w="100%"
              withCloseButton
            >
              {t('field.form.boundaryRequiredContent')}
            </Alert>
          )}
        </Transition>

        <Button disabled={!form.isDirty()} loading={loading} type="submit">
          {type === 'edit' ? t('common.save') : t('common.create')}
        </Button>
        <Button color="red" onClick={handleBack} type="button">
          {t('common.back')}
        </Button>
      </Group>
    </form>
  );
}

export default FieldForm;
